import { useState, useEffect } from "react";

export type Region = "us" | "eu" | "gulf" | "latam";
export type Language = "en" | "de" | "fr" | "es" | "ar";

interface GeolocationData {
  country: string;
  countryCode: string;
  region: Region;
  language: Language;
  currency: string;
  currencySymbol: string;
  isLoading: boolean;
  error: string | null;
}

// Country to region/language/currency mapping
const countryConfig: Record<string, { region: Region; language: Language; currency: string; symbol: string }> = {
  // North America
  US: { region: "us", language: "en", currency: "USD", symbol: "$" },
  CA: { region: "us", language: "en", currency: "CAD", symbol: "CA$" },
  
  // Europe - German speaking
  DE: { region: "eu", language: "de", currency: "EUR", symbol: "€" },
  AT: { region: "eu", language: "de", currency: "EUR", symbol: "€" },
  CH: { region: "eu", language: "de", currency: "CHF", symbol: "CHF" },
  
  // Europe - French speaking
  FR: { region: "eu", language: "fr", currency: "EUR", symbol: "€" },
  BE: { region: "eu", language: "fr", currency: "EUR", symbol: "€" },
  LU: { region: "eu", language: "fr", currency: "EUR", symbol: "€" },
  
  // Europe - Spanish speaking
  ES: { region: "eu", language: "es", currency: "EUR", symbol: "€" },
  
  // Europe - Other (default to English)
  GB: { region: "eu", language: "en", currency: "GBP", symbol: "£" },
  IE: { region: "eu", language: "en", currency: "EUR", symbol: "€" },
  NL: { region: "eu", language: "en", currency: "EUR", symbol: "€" },
  IT: { region: "eu", language: "en", currency: "EUR", symbol: "€" },
  PT: { region: "eu", language: "en", currency: "EUR", symbol: "€" },
  
  // Gulf / Middle East
  QA: { region: "gulf", language: "ar", currency: "QAR", symbol: "ر.ق" },
  AE: { region: "gulf", language: "ar", currency: "AED", symbol: "د.إ" },
  SA: { region: "gulf", language: "ar", currency: "SAR", symbol: "ر.س" },
  KW: { region: "gulf", language: "ar", currency: "KWD", symbol: "د.ك" },
  BH: { region: "gulf", language: "ar", currency: "BHD", symbol: "د.ب" },
  OM: { region: "gulf", language: "ar", currency: "OMR", symbol: "ر.ع" },
  
  // Latin America
  MX: { region: "latam", language: "es", currency: "MXN", symbol: "MX$" },
  AR: { region: "latam", language: "es", currency: "ARS", symbol: "AR$" },
  CO: { region: "latam", language: "es", currency: "COP", symbol: "CO$" },
  CL: { region: "latam", language: "es", currency: "CLP", symbol: "CL$" },
  PE: { region: "latam", language: "es", currency: "PEN", symbol: "S/" },
};

// Default fallback
const defaultConfig = { region: "eu" as Region, language: "en" as Language, currency: "EUR", symbol: "€" };

// Regional pricing (in cents for Stripe)
export const regionalPricing: Record<Region, Record<string, { price: number; currency: string }>> = {
  us: {
    starter: { price: 7900, currency: "usd" },
    boost: { price: 22900, currency: "usd" },
    launch: { price: 39900, currency: "usd" },
    momentum: { price: 59900, currency: "usd" },
    express: { price: 5900, currency: "usd" },
    extra_posters: { price: 2900, currency: "usd" },
    whatsapp_support: { price: 2900, currency: "usd" },
    logo_creation: { price: 900, currency: "usd" },
  },
  eu: {
    starter: { price: 7500, currency: "eur" },
    boost: { price: 21000, currency: "eur" },
    launch: { price: 37500, currency: "eur" },
    momentum: { price: 55000, currency: "eur" },
    express: { price: 4900, currency: "eur" },
    extra_posters: { price: 2500, currency: "eur" },
    whatsapp_support: { price: 2500, currency: "eur" },
    logo_creation: { price: 800, currency: "eur" },
  },
  gulf: {
    starter: { price: 29000, currency: "qar" },
    boost: { price: 82000, currency: "qar" },
    launch: { price: 145000, currency: "qar" },
    momentum: { price: 210000, currency: "qar" },
    express: { price: 19000, currency: "qar" },
    extra_posters: { price: 10000, currency: "qar" },
    whatsapp_support: { price: 10000, currency: "qar" },
    logo_creation: { price: 3500, currency: "qar" },
  },
  latam: {
    starter: { price: 6500, currency: "usd" },
    boost: { price: 18500, currency: "usd" },
    launch: { price: 32500, currency: "usd" },
    momentum: { price: 49500, currency: "usd" },
    express: { price: 4500, currency: "usd" },
    extra_posters: { price: 2200, currency: "usd" },
    whatsapp_support: { price: 2200, currency: "usd" },
    logo_creation: { price: 700, currency: "usd" },
  },
};

// Display prices (user-facing, without cents)
export const displayPricing: Record<Region, Record<string, number>> = {
  us: {
    starter: 79, boost: 229, launch: 399, momentum: 599,
    express: 59, extra_posters: 29, whatsapp_support: 29, logo_creation: 9,
  },
  eu: {
    starter: 75, boost: 210, launch: 375, momentum: 550,
    express: 49, extra_posters: 25, whatsapp_support: 25, logo_creation: 8,
  },
  gulf: {
    starter: 290, boost: 820, launch: 1450, momentum: 2100,
    express: 190, extra_posters: 100, whatsapp_support: 100, logo_creation: 35,
  },
  latam: {
    starter: 65, boost: 185, launch: 325, momentum: 495,
    express: 45, extra_posters: 22, whatsapp_support: 22, logo_creation: 7,
  },
};

export const useGeolocation = (): GeolocationData => {
  const [data, setData] = useState<GeolocationData>({
    country: "",
    countryCode: "",
    region: "eu",
    language: "en",
    currency: "EUR",
    currencySymbol: "€",
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const detectLocation = async () => {
      // Check localStorage for saved preference
      const savedPreference = localStorage.getItem("boost72_locale");
      if (savedPreference) {
        try {
          const parsed = JSON.parse(savedPreference);
          setData({
            ...parsed,
            isLoading: false,
            error: null,
          });
          return;
        } catch {
          // Invalid saved data, continue with detection
        }
      }

      try {
        // Use ipapi.co (free tier, HTTPS supported)
        const response = await fetch("https://ipapi.co/json/", { 
          signal: AbortSignal.timeout(3000) // 3s timeout
        });
        
        if (!response.ok) {
          throw new Error("Failed to detect location");
        }

        const result = await response.json();
        
        if (result.error) {
          throw new Error("Location detection failed");
        }
        
        const countryCode = result.country_code;

        const config = countryConfig[countryCode] || defaultConfig;

        const newData = {
          country: result.country_name || result.country,
          countryCode: countryCode,
          region: config.region,
          language: config.language,
          currency: config.currency,
          currencySymbol: config.symbol,
          isLoading: false,
          error: null,
        };

        setData(newData);
        
        // Save to localStorage
        localStorage.setItem("boost72_locale", JSON.stringify({
          country: newData.country,
          countryCode: newData.countryCode,
          region: newData.region,
          language: newData.language,
          currency: newData.currency,
          currencySymbol: newData.currencySymbol,
        }));
      } catch {
        // Silently fall back to defaults - no console error
        setData({
          country: "Germany",
          countryCode: "DE",
          region: defaultConfig.region,
          language: defaultConfig.language,
          currency: defaultConfig.currency,
          currencySymbol: defaultConfig.symbol,
          isLoading: false,
          error: null,
        });
      }
    };

    detectLocation();
  }, []);

  return data;
};

// Helper to update locale preference manually
export const setLocalePreference = (
  language: Language,
  region: Region,
  currency: string,
  currencySymbol: string
) => {
  const data = {
    country: "",
    countryCode: "",
    region,
    language,
    currency,
    currencySymbol,
  };
  localStorage.setItem("boost72_locale", JSON.stringify(data));
  // Trigger page reload to apply changes
  window.location.reload();
};
